/* KK layer behaviour: bundle picker, gallery, add to cart -> cart drawer, sliders, reviews show-more.
   Add to cart mirrors Horizon's product-form flow: POST /cart/add.js (with cart section ids),
   re-read the AJAX cart, resolve a CartLinesUpdateEvent so cart-items-component re-renders the drawer,
   then open the drawer. Errors are shown next to the button. */
import { CartLinesUpdateEvent } from '@shopify/events';

const kkMobile = () => window.matchMedia('(max-width: 749px)').matches;

const kkRoutes = () => (window.Theme && window.Theme.routes) || {};

async function kkFetchCart() {
  const response = await fetch(`${kkRoutes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Cart ${response.status}`);
  return response.json();
}

function kkOpenDrawer() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    if (!drawer.isOpen) drawer.open();
    return true;
  }
  return false;
}

/**
 * Adds one variant to the cart and opens the cart drawer.
 * @param {string|number} variantId
 * @param {number} quantity
 * @param {HTMLElement} source - element the cart event is dispatched from
 * @returns {Promise<object>} the updated AJAX cart
 */
async function kkAddToCart(variantId, quantity, source) {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', String(quantity || 1));
  if (sectionIds.length) {
    body.append('sections', sectionIds.join(','));
    body.append('sections_url', window.location.pathname);
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  let settled = false;
  (source || document).dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: quantity || 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(kkRoutes().cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body,
      credentials: 'same-origin',
    });
    const data = await response.json();
    const cart = await kkFetchCart();

    if (!response.ok || data.status) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'kk-buybox', itemCount: quantity || 1 },
      });
      settled = true;
      throw new Error(data.description || data.message || `Error ${response.status}`);
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'kk-buybox', itemCount: quantity || 1, sections: data.sections, didError: false },
    });
    settled = true;

    if (!kkOpenDrawer()) window.location.href = kkRoutes().cart_url || '/cart';
    return cart;
  } catch (error) {
    if (!settled) deferred.reject(error);
    throw error;
  }
}

window.kkAddToCart = kkAddToCart;

class KkBuybox extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    const data = this.querySelector('script[data-kk-variants]');
    this.variants = data ? JSON.parse(data.textContent) : [];
    this.form = this.querySelector('form.kk-box__form');
    this.idInput = this.form ? this.form.querySelector('input[name=id]') : null;
    this.button = this.querySelector('[data-kk-atc]');
    this.error = this.querySelector('[data-kk-error]');
    this.updateUrl = this.dataset.updateUrl === 'true';
    this.querySelectorAll('input[data-kk-variant]').forEach((input) => {
      input.addEventListener('change', () => this.select(input.value));
    });
    const checked = this.querySelector('input[data-kk-variant]:checked');
    if (checked && this.idInput && this.idInput.value !== checked.value) this.select(checked.value);
    if (this.form) this.form.addEventListener('submit', (event) => this.add(event));
  }

  get gallery() {
    const id = this.dataset.gallery;
    return id ? document.getElementById(id) : null;
  }

  select(id) {
    const v = this.variants.find((item) => String(item.id) === String(id));
    if (!v) return;
    if (this.idInput) this.idInput.value = v.id;
    const set = (sel, text) => this.querySelectorAll(sel).forEach((el) => { el.textContent = text; });
    set('[data-kk-price]', v.price);
    const unit = this.querySelector('[data-kk-unit]');
    if (unit) { unit.textContent = v.unit; unit.hidden = !v.unit; }
    const compare = this.querySelector('[data-kk-compare]');
    if (compare) { compare.textContent = v.compare; compare.hidden = !v.compare; }
    const save = this.querySelector('[data-kk-save]');
    if (save) { save.textContent = v.save; save.hidden = !v.save; }
    if (this.button) {
      this.button.disabled = !v.available;
      const label = this.button.querySelector('[data-kk-atc-label]');
      if (label) label.textContent = v.available ? this.button.dataset.addLabel : this.button.dataset.soldLabel;
    }
    if (this.error) this.error.textContent = '';
    const gallery = this.gallery;
    if (v.image && gallery && gallery.showImage) gallery.showImage(v.image);
    if (this.updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }

  async add(event) {
    event.preventDefault();
    if (!this.idInput || !this.button || this.button.disabled) return;
    const checked = this.querySelector('input[data-kk-variant]:checked');
    const id = checked ? checked.value : this.idInput.value;
    this.button.setAttribute('aria-busy', 'true');
    this.button.disabled = true;
    if (this.error) this.error.textContent = '';
    try {
      await kkAddToCart(id, 1, this);
    } catch (error) {
      if (this.error) this.error.textContent = `${this.dataset.errorText || ''} ${(error && error.message) || ''}`.trim();
    } finally {
      this.button.removeAttribute('aria-busy');
      const current = this.variants.find((item) => String(item.id) === String(id));
      this.button.disabled = current ? !current.available : false;
    }
  }
}

class KkGallery extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.track = this.querySelector('[data-kk-track]');
    this.slides = Array.from(this.querySelectorAll('[data-kk-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-kk-thumb]'));
    this.mainImg = this.slides[0] ? this.slides[0].querySelector('img') : null;
    const prev = this.querySelector('[data-kk-prev]');
    const next = this.querySelector('[data-kk-next]');
    if (prev) prev.addEventListener('click', () => this.go(this.index() - 1));
    if (next) next.addEventListener('click', () => this.go(this.index() + 1));
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i)));
    if (this.track) this.track.addEventListener('scroll', () => this.mark(this.index()), { passive: true });
  }

  index() {
    if (!kkMobile()) {
      const active = this.slides.findIndex((s) => s.classList.contains('is-active'));
      return active < 0 ? 0 : active;
    }
    if (!this.track || !this.track.clientWidth) return 0;
    return Math.round(this.track.scrollLeft / this.track.clientWidth);
  }

  go(i) {
    const n = this.slides.length;
    if (!n) return;
    const target = (i + n) % n;
    if (kkMobile() && this.track) this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
    this.slides.forEach((s, k) => s.classList.toggle('is-active', k === target));
    this.mark(target);
  }

  mark(i) {
    this.thumbs.forEach((t, k) => {
      t.classList.toggle('is-active', k === i);
      t.setAttribute('aria-current', k === i ? 'true' : 'false');
    });
  }

  showImage(image) {
    if (!this.mainImg) return;
    this.mainImg.src = image.src;
    if (image.srcset) this.mainImg.srcset = image.srcset; else this.mainImg.removeAttribute('srcset');
    if (image.alt !== undefined) this.mainImg.alt = image.alt;
    const thumbImg = this.thumbs[0] ? this.thumbs[0].querySelector('img') : null;
    if (thumbImg) { thumbImg.src = image.thumb || image.src; thumbImg.removeAttribute('srcset'); }
    this.go(0);
  }
}

class KkSlider extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.track = this.querySelector('[data-kk-track]');
    const step = (dir) => {
      if (!this.track) return;
      const item = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const width = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
      const max = this.track.scrollWidth - this.track.clientWidth;
      let left = this.track.scrollLeft + dir * width;
      if (left > max + 4) left = 0;
      if (left < -4) left = max;
      this.track.scrollTo({ left, behavior: 'smooth' });
    };
    this.querySelectorAll('[data-kk-prev]').forEach((b) => b.addEventListener('click', () => step(-1)));
    this.querySelectorAll('[data-kk-next]').forEach((b) => b.addEventListener('click', () => step(1)));
  }
}

class KkReviews extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.items = Array.from(this.querySelectorAll('[data-kk-review]'));
    this.button = this.querySelector('[data-kk-more]');
    this.step = () => (kkMobile() ? Number(this.dataset.mobile || 5) : Number(this.dataset.desktop || 6));
    this.limit = this.step();
    this.render();
    if (this.button) {
      this.button.addEventListener('click', () => {
        const first = this.limit;
        this.limit += this.step();
        this.render();
        const target = this.items[first];
        if (target) { target.setAttribute('tabindex', '-1'); target.focus({ preventScroll: true }); }
      });
    }
  }

  render() {
    this.items.forEach((item, i) => { item.hidden = i >= this.limit; });
    if (this.button) this.button.hidden = this.items.length <= this.limit;
  }
}

if (!customElements.get('kk-buybox')) customElements.define('kk-buybox', KkBuybox);
if (!customElements.get('kk-gallery')) customElements.define('kk-gallery', KkGallery);
if (!customElements.get('kk-slider')) customElements.define('kk-slider', KkSlider);
if (!customElements.get('kk-reviews')) customElements.define('kk-reviews', KkReviews);
